/* eslint-disable require-atomic-updates */
/* istanbul ignore file */
/* eslint-disable no-undefined */

import { get } from 'js-cookie';

import { integrationsBaseUrl } from '../globals';
import { lastChatbotInteractionWithin } from '../helpers/chatbotSession';
import { oneDay } from '../helpers/date';
import { domReady, injectScriptTag, injectStyleTag } from '../helpers/dom';
import { waitForGoogleAnalytics } from '../helpers/misc';
import { chatbotModalIntegrationCookieName } from '../integrations/modal/config';

let renderChatbot: boolean;
let chatbotHasBeenInjected = false;
const clientVersion = CLIENT_TARGET_VERSION ? CLIENT_TARGET_VERSION : '1281';
const liveEnvironment = CLIENT_TARGET_ENV ? CLIENT_TARGET_ENV : 'live1';

function createClientUrl(): Promise<string> {
	let chatbotClientUrl = CHATBOT_CLIENT_BASE_URL;

	const environmentOverride = get('ChatbotEnvironmentOverride');
	const clientVersionOverride = get('ChatbotClientVersionOverride');

	chatbotClientUrl = clientVersionOverride
		? chatbotClientUrl.replace('{CLIENT_TARGET_VERSION}', clientVersionOverride)
		: chatbotClientUrl.replace('{CLIENT_TARGET_VERSION}', clientVersion);

	if (environmentOverride) {
		return Promise.resolve(
			chatbotClientUrl.replace('{CLIENT_TARGET_ENV}', environmentOverride)
		);
	}

	if (window.location.hostname.indexOf('ao.com') > -1) {
		const enableChatbotQA = get('EnableChatbotQA');
		const qaEnv = liveEnvironment === 'live1' ? 'live2' : 'live1';

		chatbotClientUrl = enableChatbotQA
			? chatbotClientUrl.replace('{CLIENT_TARGET_ENV}', qaEnv)
			: chatbotClientUrl.replace('{CLIENT_TARGET_ENV}', liveEnvironment);

		return Promise.resolve(chatbotClientUrl);
	}

	const env =
		window.location.hostname.indexOf('beta') > -1 ? 'beta' : 'staging';

	return Promise.resolve(chatbotClientUrl.replace('{CLIENT_TARGET_ENV}', env));
}

async function injectChatbot() {
	// Exposing chatbotInjectionHasRunAndInjectedScripts to DY to conditionally try
	// and inject the client scripts again if the injection script loads before DY
	window.chatbotInjectionHasRunAndInjectedScripts = true;

	const chatbotClientUrl = await createClientUrl();

	if (window.AOReact && window.AOReactDOM && window.digitalDataProxy) {
		const clientBasePath = `${chatbotClientUrl}/without-externals`;
		injectStyleTag(`${clientBasePath}/chatbot.css`, 'chatbot-styles');
		injectScriptTag(`${clientBasePath}/chatbot.js`, 'chatbot-script');
	} else if (window.AOReact && window.AOReactDOM && !window.digitalDataProxy) {
		const clientBasePath = `${chatbotClientUrl}/with-digitalDataProxy`;
		injectStyleTag(`${clientBasePath}/chatbot.css`, 'chatbot-styles');
		injectScriptTag(`${clientBasePath}/chatbot.js`, 'chatbot-script');
	} else {
		const clientBasePath = `${chatbotClientUrl}/with-externals`;
		injectStyleTag(`${clientBasePath}/chatbot.css`, 'chatbot-styles');
		injectScriptTag(`${clientBasePath}/chatbot.js`, 'chatbot-script');
	}

	chatbotHasBeenInjected = true;
}

async function pdpSpecTooltipsIntegration(): Promise<void> {
	injectStyleTag(
		`${integrationsBaseUrl}/pdp-spec-tooltips/index.css`,
		'pdp-spec-tooltips-styles'
	);
	await injectScriptTag(
		`${integrationsBaseUrl}/pdp-spec-tooltips/index.js`,
		'pdp-spec-tooltips-script'
	);

	return Promise.resolve();
}

async function pdpHelpAndAdviceIntegration(): Promise<void> {
	injectStyleTag(
		`${integrationsBaseUrl}/pdp-help-and-advice/index.css`,
		'pdp-help-and-advice-styles'
	);
	await injectScriptTag(
		`${integrationsBaseUrl}/pdp-help-and-advice/index.js`,
		'pdp-help-and-advice-script'
	);

	return Promise.resolve();
}

async function helpAndAdviceIntegration(): Promise<void> {
	injectStyleTag(
		`${integrationsBaseUrl}/help-and-advice/index.css`,
		'help-and-advice-styles'
	);
	await injectScriptTag(
		`${integrationsBaseUrl}/help-and-advice/index.js`,
		'help-and-advice-script'
	);

	return Promise.resolve();
}

async function modalIntegration(): Promise<void> {
	injectStyleTag(`${integrationsBaseUrl}/modal/index.css`, 'modal-styles');
	await injectScriptTag(
		`${integrationsBaseUrl}/modal/index.js`,
		'modal-script'
	);

	return Promise.resolve();
}

type IntegrationFn = () => Promise<void>;

interface InjectionIntegration {
	shouldRender: boolean;
	integrationFuncs?: IntegrationFn[];
}

async function getInjectionInstruction(): Promise<InjectionIntegration> {
	const currentBreakpoint =
		window.breakpoint && window.breakpoint.value
			? window.breakpoint.value
			: 'mobile';
	const pathnameSegments = window.location.pathname
		.split('/')
		.filter((s) => s.length > 0);

	const chatbotModalHasBeenDismissed = get(chatbotModalIntegrationCookieName);

	let shouldRender = false;
	const integrationFuncs = [];

	if (
		currentBreakpoint !== 'mobile' &&
		pathnameSegments.length > 1 &&
		pathnameSegments[0] === 'product' &&
		pathnameSegments[1].indexOf('washing-machine') > -1 &&
		window.aoProductPageMaxymiser &&
		window.aoProductPageMaxymiser.fitType === 'Free Standing' &&
		!window.aoProductPageMaxymiser.isPremium
	) {
		// Washing Machine PDP with highlighted features
		shouldRender = true;
		integrationFuncs.push(pdpSpecTooltipsIntegration);
	}

	if (
		window.chatbotPdpSpecTooltips === true &&
		currentBreakpoint !== 'mobile' &&
		pathnameSegments.length > 1 &&
		pathnameSegments[0] === 'product' &&
		pathnameSegments[1].indexOf('108.aspx') > -1 &&
		!window.aoProductPageMaxymiser.isPremium
	) {
		// TV PDP with highlighted features
		shouldRender = true;
		integrationFuncs.push(pdpSpecTooltipsIntegration);
	}

	if (
		window.chatbotPdpHelpAndAdvice === true &&
		pathnameSegments.length > 1 &&
		pathnameSegments[0] === 'product' &&
		pathnameSegments[1].indexOf('108.aspx') > -1 &&
		document.querySelector('[data-tag-section="help and advice"]')
	) {
		// TV PDP with Help & Advice section
		shouldRender = true;
		integrationFuncs.push(pdpHelpAndAdviceIntegration);
	}

	if (pathnameSegments[0] === 'help-and-advice') {
		// All help and advice pages
		shouldRender = true;
		integrationFuncs.push(helpAndAdviceIntegration);
	}

	// eslint-disable-next-line no-constant-condition
	if (window.chatbotModal === true) {
		shouldRender = true;
		if (!chatbotModalHasBeenDismissed) {
			integrationFuncs.push(modalIntegration);
		}
	}

	return Promise.resolve({
		shouldRender,
		integrationFuncs,
	});
}

window.addEventListener('chatbotReady', () => {
	if (renderChatbot) {
		window.AOChatbot.render();
	}
});

async function runPageIntegration(): Promise<boolean> {
	return new Promise(async (resolve) => {
		const { shouldRender, integrationFuncs } = await getInjectionInstruction();
		if (shouldRender) {
			if (integrationFuncs && integrationFuncs.length) {
				try {
					await Promise.all(
						integrationFuncs.map((integrationFunc) => {
							return integrationFunc();
						})
					);
				} catch (error) {
					// console.log(error);
				}
			}
		}
		return resolve(shouldRender);
	});
}

function handleVisibilityChange(): void {
	if (chatbotHasBeenInjected) {
		return;
	}
	if (document.visibilityState === 'visible') {
		if (renderChatbot || lastChatbotInteractionWithin(oneDay)) {
			injectChatbot();
		}
	}
}

window.AOChatbot = {
	...window.AOChatbot,
	injectScripts: () => {
		domReady(async () => {
			// TODO: Remove this condition when Dynamic Yield contract ends as it will become redundant
			if (window.DY && window.DY.API) {
				await new Promise<void>((resolve) => {
					// eslint-disable-next-line new-cap
					window.DY.API('callback', async () => {
						// DY currently waits for Google Analytics to exist/be ready before running its test variants
						// so we also need to wait :/
						await waitForGoogleAnalytics(60, 500);
						renderChatbot = await runPageIntegration();
						if (window.chatbotMvtExists) {
							renderChatbot = true;
						}
						return resolve();
					});
				});
			} else {
				renderChatbot = await runPageIntegration();
				if (window.chatbotMvtExists) {
					renderChatbot = true;
				}
			}
			document.addEventListener(
				'visibilitychange',
				handleVisibilityChange as EventListener
			);
			if (renderChatbot || lastChatbotInteractionWithin(oneDay)) {
				injectChatbot();
			}
		});
	},
};

window.AOChatbot.injectScripts();
